<template>
  <responsive-drawer-layout>
    <template v-slot:content>
      <div v-if="questionnaire" class="inherit-height bf-result-container full-width overflow-x-hidden pa-1 pa-md-5">
        <div class="rating-container">
          <p class="font-weight-bold title">{{ questionnaireTypeString }} - {{ questionnaireTitleString }}</p>
          <v-row v-if="metaFields.length > 0">
            <div v-for="(metaField, index) in metaFields" :key="`meta-field-${index}`" class="col-xs-12 col-sm-6 col-md-4">
              <section :class="sectionClass">
                <p class="caption">{{ metaField.label }}</p>
                <p v-if="metaField.type == 'fixed'" class="body-1 mb-0">{{ metaField.value }}</p>
                <p v-else-if="metaField.type == 'prefilled' && metaField.value == 'name'">{{ currentUser.firstname }} {{ currentUser.lastname }}</p>
                <p v-else-if="metaField.type == 'prefilled' && metaField.value == 'employeenr'">{{ currentUser.employeenr }}</p>
                <p v-else-if="metaField.type == 'prefilled' && metaField.value == 'boss'">
                  <template v-if="bosses.length === 0" class="body-1 mb-0">>-</template>
                  <template v-else>
                    <p v-for="boss in bosses" :key="boss" class="body-1 mb-0">{{ boss }}</p>
                  </template>
                </p>
                <p v-else-if="metaField.type == 'prefilled' && metaField.value == 'evaluator'">{{ teamleader.firstname }} {{ teamleader.lastname }}</p>
                <p v-else-if="metaField.type == 'prefilled' && metaField.value == 'username'">{{ currentUser.username }}</p>
                <p v-else-if="metaField.type == 'prefilled' && metaField.value == 'email'">{{ currentUser.email }}</p>
              </section>
            </div>
            <div class="col-xs-12 col-sm-6 col-md-4">
              <section class="pa-4 op-panel rounded-lg">
                <p class="caption">{{ $t('bf.date_time') }}</p>
                <p class="body-1 mb-0">{{ ratingDateTimeString }}</p>
              </section>
            </div>
          </v-row>
          <v-row v-else class="mb-3">
            <div class="col-xs-12 col-sm-6 col-md-4">
              <section class="pa-4 op-panel rounded-lg">
                <p class="caption">{{ $t('employee.moduleTitle') }}</p>
                <p class="body-1 mb-0">{{ currentUser.firstname }} {{ currentUser.lastname }}</p>
              </section>
            </div>
            <div class="col-xs-12 col-sm-6 col-md-4">
              <section :class="sectionClass">
                <p class="caption">{{ $t('employeenr') }}</p>
                <p v-if="currentUser.employeenr" class="body-1 mb-0">{{ currentUser.employeenr }}</p>
                <p v-else class="body-1 mb-0">-</p>
              </section>
            </div>
            <div class="col-xs-12 col-sm-6 col-md-4">
              <section class="pa-4 op-panel rounded-lg">
                <p class="caption">{{ $t('bf.teamleader') }}</p>
                <p v-if="bosses.length === 0" class="body-1 mb-0">-</p>
                <template v-else>
                  <p v-for="boss in bosses" :key="boss" class="body-1 mb-0">{{ boss }}</p>
                </template>
              </section>
            </div>
            <div class="col-xs-12 col-sm-6 col-md-4">
              <section class="pa-4 op-panel rounded-lg">
                <p class="caption">{{ $t('bf.evaluator') }}</p>
                <p class="body-1 mb-0">{{ teamleader.firstname }} {{ teamleader.lastname }}</p>
              </section>
            </div>
            <div class="col-xs-12 col-sm-6 col-md-4">
              <section class="pa-4 op-panel rounded-lg">
                <p class="caption">{{ $t('bf.date_time') }}</p>
                <p class="body-1 mb-0">{{ ratingDateTimeString }}</p>
              </section>
            </div>
          </v-row>

          <div class="d-flex justify-center">
            <v-switch v-model="showLabels" :label="$t('bf.showLabels')" class="ma-0"></v-switch>
          </div>
          <v-expansion-panels flat v-model="openPanels" multiple class="full-width overflow-hidden">
            <!--- additional fields --->
            <v-expansion-panel v-if="additionalFieldsAndAnswers" class="panel-border">
              <v-expansion-panel-header class="text-center panel-content-border">
                <p class="headline-title ma-1">{{ $t('wb-config.basisdaten.navigationTitle') }}</p>
              </v-expansion-panel-header>

              <v-expansion-panel-content class="pt-2 pa-0">
                <div class="expansion-panel-inner-container">
                  <v-simple-table>
                    <template v-slot:default>
                      <tbody>
                        <tr v-for="(field, index) in additionalFieldsAndAnswers" :key="field.id">
                          <td class="body-1 ma-1" width="150">{{ field.title }}:</td>
                          <td class="body-1 ma-1">{{ field.answer }}</td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!--- begleitfahrten - only for mitarbeitergespräche --->
            <v-expansion-panel v-if="isQuestionnaireTypeEvaluation" class="panel-border">
              <v-expansion-panel-header class="text-center panel-content-border">
                <span class="headline-title ma-1">{{ $t('bf.begleitfahrten') }}</span>
              </v-expansion-panel-header>

              <v-expansion-panel-content class="pt-2">
                <div class="expansion-panel-inner-container">
                  <v-simple-table>
                    <template v-slot:default>
                      <tbody>
                        <tr>
                          <td class="body-1 ma-1">{{ $t('bf.begleitfahrten_average') }}:</td>
                          <td class="body-1 ma-1">{{ evaluationBegleitfahrtenAverage }}</td>
                        </tr>
                        <tr>
                          <td class="body-1 ma-1">{{ $t('bf.note') }}:</td>
                          <td class="body-1 ma-1">{{ ratingNote }}</td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!--- questions and answers --->
            <v-expansion-panel v-for="section in questionnaire.sections" :key="section.id" class="panel-border">
              <v-expansion-panel-header class="text-center panel-content-border">
                <span v-if="getSectionGrade(section.id)" class="headline-title ma-1">{{ section.title }} (Ø {{ getSectionGrade(section.id) }})</span>
                <span v-else class="headline-title ma-1">{{ section.title }}</span>
              </v-expansion-panel-header>

              <v-expansion-panel-content>
                <div v-for="topic in section.topics" :key="topic.id + '!'" class="mb-3 expansion-panel-inner-container">
                  <!-- calling a method for decision runs the method once and later on again for processing - not efficient -->
                  <!--                  <div v-if="getfilteredQuestionsAndAnswersByTopic(topic.id).length">-->
                  <responsive-result-table
                    :topic="topic"
                    :questions-and-answers="getfilteredQuestionsAndAnswersByTopic(topic.id)"
                    :show-labels="showLabels"
                  ></responsive-result-table>
                </div>
                <div v-if="isQuestionnaireTypeQuestionnaire" class="expansion-panel-inner-container">
                  <p v-if="getSectionGrade(section.id)" class="font-weight-bold body-1 ma-2">
                    {{ $t('average') }} {{ section.title }}: Ø {{ getSectionGrade(section.id) }}
                  </p>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
          <p v-if="isQuestionnaireTypeEvaluation && evaluationRatingGrade" class="title ma-2 mt-5">{{ $t('bf.total') }}: Ø {{ evaluationRatingGrade }}</p>
          <p v-if="isQuestionnaireTypeQuestionnaire && questionnaireRatingGrade" class="title ma-2 mt-5">
            {{ $t('bf.total') }}: Ø {{ questionnaireRatingGrade }}
          </p>
          <p v-if="ratingComment" class="body-1 ma-2">{{ $t('module.wagenbuch.comment') }}: {{ ratingComment }}</p>
          <v-divider></v-divider>
        </div>
        <!--- chart ---->
        <div v-if="isQuestionnaireTypeQuestionnaire" class="mt-5">
          <begleitfahrt-resultat-chart
            :chart-data="chartData.data"
            :chart-labels="chartData.labels"
            :chart-colors="chartData.colors"
            :chart-options="chartOptions"
          ></begleitfahrt-resultat-chart>
        </div>
      </div>
    </template>
  </responsive-drawer-layout>
</template>
<script>
import BegleitfahrtResultatChart from '@/modules/begleitfahrt/components/BegleitfahrtResultatChart.vue';
import common from '../../../../mixins/common';
import dateUtils from '@/common/dateUtils';
import { mapState } from 'vuex';
import ResponsiveDrawerLayout from '@/layouts/ResponsiveDrawerLayout.vue';
import ResponsiveResultTable from '@/modules/begleitfahrt/views/result/ResponsiveResultTable.vue';
import config from '@/common/config';

export default {
  name: 'BegleitfahrtResultView',
  components: { ResponsiveResultTable, BegleitfahrtResultatChart, ResponsiveDrawerLayout },
  mixins: [common],
  data() {
    return {
      openPanels: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10], // TODO: remove this when we have a better solution
      chartOptions: {
        chart: {
          type: 'donut',
          width: 400,
          foreColor: '', // Has to be set to overwrite the default color for it to use the global color
        },
      },
      showLabels: true,
    };
  },
  computed: {
    ...mapState('begleitfahrt', [
      'ratingById',
      'userById',
      'questionnaireById',
      'additionalFieldById',
      'additionalFieldResultById',
      'questionnaireQuestionById',
      'questionnaireTopicById',
      'questionnaireQuestionResultById',
      'questionnaireAnswerDescription',
      'answerSetsById',
      'reportMedia',
      'answerById',
      'config',
      'deviceUserBoss',
    ]),
    metaFields() {
      if (Array.isArray(this.config) && this.config.length > 0) {
        if (this.config[0].jsonValue && this.config[0].jsonValue.metaFields) {
          return this.config[0].jsonValue.metaFields;
        }
      }
      return [];
    },
    sectionClass() {
      return this.darkMode ? 'pa-4 grey darken-3 rounded-lg' : 'pa-4 grey lighten-3 rounded-lg';
    },
    bosses() {
      const bossIds = this.deviceUserBoss.filter((boss) => boss.userId === Number(this.currentUserId));
      return bossIds
        .filter((boss) => this.userById[boss.bossId] !== undefined)
        .map((boss) => this.userById[boss.bossId].firstname + ' ' + this.userById[boss.bossId].lastname);
    },
    tenant: () => config.tenant,
    isTenantZVB() {
      return this.tenant === 'zvb';
    },
    currentUserId() {
      return this.$route.params.userId;
    },
    currentRatingId() {
      return this.$route.params.ratingId;
    },
    rating() {
      return this.ratingById[this.currentRatingId];
    },
    questionnaire() {
      if (this.rating && this.rating.questionnaireIdFk) {
        return this.questionnaireById[this.rating.questionnaireIdFk];
      }
      return null;
    },
    questionnaireTitleString() {
      return this.questionnaire.title;
    },
    questionnaireType() {
      return this.questionnaire.isType;
    },
    isQuestionnaireTypeEvaluation() {
      return this.questionnaireType === 'evaluation';
    },
    isQuestionnaireTypeQuestionnaire() {
      return this.questionnaireType === 'questionnaire';
    },
    questionnaireTypeString() {
      const type = this.questionnaireType;
      if (type === 'questionnaire') {
        return this.$t('bf.questionnaire');
      } else if (type === 'evaluation') {
        return this.$t('bf.evaluation');
      } else {
        return '';
      }
    },
    currentUser() {
      return this.userById[this.currentUserId];
    },
    teamleader() {
      return this.userById[this.rating.teamleiterIdFk];
    },
    ratingNote() {
      return this.rating.note === '' ? '-' : this.rating.note;
    },
    ratingComment() {
      return this.rating.comment !== '' ? this.rating.comment : null;
    },

    // returns the average of all begleitfahrt ratings associated with an evaluation
    evaluationBegleitfahrtenAverage() {
      const meta = this.rating.jsonMeta ?? null;

      if (meta?.evaluationReportIds) {
        const metaArray = meta.evaluationReportIds.split(',');

        const gradeArray = metaArray
          .map((uuid) => {
            const rating = Object.values(this.ratingById).find((r) => r.uuid === uuid);
            return rating && rating.grade ? rating.grade : null;
          })
          .filter((grade) => grade !== null);

        if (gradeArray.length === 0) return null;

        const sum = gradeArray.reduce((a, b) => a + b, 0);
        const average = sum / gradeArray.length;

        return Number(average.toFixed(2));
      }
      return 0;
    },
    additionalFieldsAndAnswers() {
      let additionalFields = Object.values(this.additionalFieldById).filter((f) => {
        return !this.questionnaire.hiddenAdditionalFieldIds.includes(f.id);
      });

      let filteredAdditionalFields = additionalFields.filter((field) => {
        const answer = Object.values(this.additionalFieldResultById).find((r) => r.additionalFieldIdFk == field.id && r.ratingIdFk == this.currentRatingId);
        if (answer && answer.value) {
          field.answer = answer.value;
          return true;
        }
        return false;
      });

      filteredAdditionalFields.sort((a, b) => a.sort - b.sort);

      return filteredAdditionalFields.length > 0 ? filteredAdditionalFields : null;
    },
    questionnaireRatingGrade() {
      const firstSectionId = this.questionnaire.sections[0].id;
      const questionnaireSections = this.questionnaire.sections;
      let totalSum;
      let countSections;
      let average;

      const firstSectionGrade = this.getSectionGrade(firstSectionId);
      if (firstSectionGrade) {
        const isGradeNumber = !isNaN(firstSectionGrade);
        if (isGradeNumber) {
          totalSum = parseFloat(firstSectionGrade);
          countSections = 1;
        }
      }

      questionnaireSections.forEach((section) => {
        const sectionGrade = this.getSectionGrade(section.id);
        if (sectionGrade) {
          const isGradeNumber = !isNaN(sectionGrade);
          if (isGradeNumber) {
            totalSum += parseFloat(sectionGrade);
            countSections++;
          }
        }
      });

      if (this.evaluationBegleitfahrtenAverage && !isNaN(this.evaluationBegleitfahrtenAverage)) {
        totalSum += parseFloat(this.evaluationBegleitfahrtenAverage);
        countSections++;
      }

      if (totalSum && countSections) {
        average = Math.ceil((totalSum / countSections) * 100) / 100;
      }

      return average ? average : null;
    },
    evaluationRatingGrade() {
      let answersArray = [];
      let bonusArray = [];
      let questionnaireQuestionResults = Object.values(this.questionnaireQuestionResultById).filter((r) => r.ratingIdFk == this.currentRatingId);

      questionnaireQuestionResults.forEach((r) => {
        const answer = this.answerById[r.answerIdFK];
        if (!answer) {
          return 0;
        }
        const answerSet = this.answerSetsById[answer.answerSetIdFk];
        const averageBoolean = answerSet.average !== 0;

        if (answer && answer.answer && !isNaN(answer.answer) && averageBoolean === true) {
          // check if it's a bonus question
          const question = this.questionnaireQuestionById[r.questionIdFk];
          const topic = this.questionnaireTopicById[question.topicIdFk];

          if (topic.title.toLowerCase() === 'bonus' && this.isTenantZVB) {
            bonusArray.push(parseFloat(answer.answer));
          } else {
            answersArray.push(parseFloat(answer.answer));
          }
        }
      });
      if (answersArray.length === 0) return 0;

      const sum = answersArray.reduce((a, b) => a + b, 0);
      const bonusSum = bonusArray.reduce((a, b) => a + b, 0);
      const evaluationSum = this.evaluationBegleitfahrtenAverage ? this.evaluationBegleitfahrtenAverage : 0;

      const totalSum = sum + bonusSum + evaluationSum;

      const average = totalSum / (answersArray.length + (evaluationSum ? 1 : 0));

      return average.toFixed(2);
    },
    ratingDateTimeString() {
      if (this.rating && this.rating.clientdate) {
        return dateUtils.reformatDateIsoStringAsYYYYMMDDHHmm(this.rating.clientdate);
      }
      return '';
    },
    chartData() {
      const questions = Object.values(this.questionnaireQuestionById).filter((q) => q.questionnaireIdFk === this.questionnaire.id);
      let answerCounts = {};
      let labelToColor = {};

      questions.forEach((q) => {
        let answerSet = this.answerSetsById[q.answerSetIdFk];
        if (!answerSet) return;
        let answer = this.questionResultByQuestionIdAndRatingId[q.id + '_' + this.currentRatingId];
        let chosenAnswer = answer ? answerSet.answers.find((a) => a.id == answer.answerIdFK) : '';
        if (chosenAnswer && chosenAnswer.answer) {
          let answerValue = chosenAnswer.answer;
          answerCounts[answerValue] = (answerCounts[answerValue] || 0) + 1;

          if (!labelToColor[answerValue] && chosenAnswer.color) {
            labelToColor[answerValue] = chosenAnswer.color;
          }
        }
      });
      const labels = Object.keys(answerCounts);
      const data = Object.values(answerCounts);
      const colors = labels.map((label) => labelToColor[label] || '');

      return {
        labels: labels,
        colors: colors,
        data: data,
      };
    },
    questionResultByQuestionIdAndRatingId() {
      let map = {};
      Object.values(this.questionnaireQuestionResultById).forEach((i) => {
        map[i.questionIdFk + '_' + i.ratingIdFk] = i;
      });
      return map;
    },
  },
  methods: {
    getfilteredQuestionsAndAnswersByTopic(topicId) {
      let questions = Object.values(this.questionnaireQuestionById).filter((q) => q.topicIdFk === topicId);
      let results = [];
      let defaultScaleTopicsCount = 0;
      questions.forEach((q) => {
        let description = '';
        let chosenAnswerString = '';
        let comment = '';
        let filenames = [];

        let answerSet = this.answerSetsById[q.answerSetIdFk];
        let answer = this.questionResultByQuestionIdAndRatingId[q.id + '_' + this.currentRatingId];

        let chosenAnswer = '';
        if (q.answerSetIdFk !== 0) {
          chosenAnswer = answer ? answerSet.answers.find((a) => a.id == answer.answerIdFK) : '';
        }

        //if (chosenAnswer && chosenAnswer.answer) chosenAnswerString = chosenAnswer.answer;
        chosenAnswerString = chosenAnswer && chosenAnswer.answer;

        // check if any images are attached to the answer
        const images = this.reportMedia.filter((media) => media.useridfk == this.currentUserId && media.answeridfk === answer.id);
        filenames = images.map((image) => image.filename);

        if (answer && answer.comment) comment = answer.comment;

        let answerDescriptionScale = [];
        if (chosenAnswer && chosenAnswer.id) {
          let answerDescription = this.questionnaireAnswerDescription.find((desc) => desc.questionIdFk === q.id && desc.answerIdFk === chosenAnswer.id);
          description = answerDescription ? answerDescription.description : '';

          answerDescriptionScale = this.questionnaireAnswerDescription
            .filter((desc) => desc.questionIdFk === q.id)
            .map((desc) => {
              let r = answerSet.answers.find((a) => a.id === desc.answerIdFk);
              //if (!desc.description) return null;
              return {
                rating: r,
                ratingDescription: desc.description ? desc.description : '',
              };
            })
            .filter((item) => !!item)
            .sort((a, b) => {
              if (a.rating && b.rating) return a.rating.sort - b.rating.sort;
              return 0;
            });
        }
        if (answerDescriptionScale.length === 0) defaultScaleTopicsCount++;
        results.push({
          questionTitle: q.title,
          questionText: q.text,
          answer: chosenAnswerString,
          comment: comment,
          answerDescription: description,
          images: filenames,
          answerDescriptionScale,
        });
      });

      return { defaultScaleTopicsCount, results };
    },

    getSectionGrade(sectionId) {
      let questions = Object.values(this.questionnaireQuestionById).filter((q) => q.sectionIdFk === sectionId);
      let grades = [];
      let bonusGrades = [];

      questions.forEach((q) => {
        let answerSet = this.answerSetsById[q.answerSetIdFk];
        if (!answerSet) return null;

        let answer = this.questionResultByQuestionIdAndRatingId[q.id + '_' + this.currentRatingId];
        let chosenAnswer = answer ? answerSet.answers.find((a) => a.id == answer.answerIdFK) : null;
        const averageBoolean = answerSet.average !== 0;

        if (chosenAnswer == null) return null;

        const topicTitle = this.questionnaireTopicById[q.topicIdFk].title;

        const chosenAnswerAnswer = chosenAnswer.answer;
        if (chosenAnswerAnswer && averageBoolean === true && !isNaN(chosenAnswerAnswer)) {
          if (topicTitle.toLowerCase() === 'bonus' && this.isTenantZVB) {
            bonusGrades.push(parseFloat(chosenAnswerAnswer));
          } else {
            grades.push(parseFloat(chosenAnswerAnswer));
          }
        }
      });

      if (grades.length === 0) return 0;

      const sum = grades.reduce((a, b) => a + b, 0);
      const bonusSum = bonusGrades.reduce((a, b) => a + b, 0);
      const average = (sum + bonusSum) / grades.length;
      if (isNaN(average)) {
        return null;
      }
      return average.toFixed(2);
    },
  },
};
</script>
<style scoped>
.bf-result-container {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
}
.rating-container {
  max-width: 1000px;
  width: 100%;
}
.headline-title {
  font-weight: bold;
  text-transform: uppercase;
  font-size: 18px;
  color: black;
}

.theme--dark .headline-title {
  color: white;
}

.expansion-panel-inner-container {
  margin-left: -20px;
  margin-right: -20px;
}

.theme--light .panel-border {
  border: 1px solid #dddddd;
  margin: 0.6em 0;
}

.theme--light .panel-content-border {
  background-color: #f5f5f5;
  border-bottom: 1px solid #dddddd;
}

.theme--dark .panel-border {
  border: 1px solid #282828;
  margin: 0.6em 0;
}

.theme--dark .panel-content-border {
  background-color: #282828;
  border-bottom: 1px solid #282828;
}

.theme--dark tbody > tr:hover {
  background-color: #333333 !important;
}
</style>
